import { defineStore } from 'pinia'
import type { ClientDetailsDto } from '~/generated-api-client'
import type { MwBffSessionResponse, MwBffAccountSelectionResponse, MwBffTwoFactorResponse } from '~/types/mw-bff'

export const useUserStore = defineStore("user", () => {
  const { $i18n } = useNuxtApp()
  const user = ref<ClientDetailsDto | null>(null)
  const sessionStatus = ref<'unknown' | 'authenticated' | 'unauthenticated'>('unknown')

  const getUser = computed((): ClientDetailsDto | undefined => user.value ?? undefined)

  async function login(email: string, password: string, rememberMe?: boolean, clientId?: number) {
    const { $mwApi } = useNuxtApp()
    const { deviceId } = useDeviceId()
    const { isCapacitor, setSessionId, clearSession } = useBffSession()

    try {
      const response = await $mwApi.login({ email, password, deviceId, rememberMe, clientId })
      const data = response.data as unknown as MwBffSessionResponse | MwBffAccountSelectionResponse | MwBffTwoFactorResponse

      if ('result' in data && data.result === 'account_selection_required') {
        return data as MwBffAccountSelectionResponse
      }

      if ('result' in data && data.result === 'two_factor_required') {
        return data as MwBffTwoFactorResponse
      }

      const sessionData = data as MwBffSessionResponse
      const client = sessionData.client as ClientDetailsDto

      if (isCapacitor) {
        if (sessionData.sessionId) {
          await setSessionId(sessionData.sessionId)
        }
        else {
          await clearSession()
        }
      }

      user.value = client
      sessionStatus.value = 'authenticated'

      return sessionData
    } catch (error: any) {
      setSessionStatus('unauthenticated')
      throw error
    }
  }

  async function applySessionData(sessionData: MwBffSessionResponse) {
    const { isCapacitor, setSessionId, clearSession } = useBffSession()
    const client = sessionData.client as ClientDetailsDto

    if (isCapacitor) {
      if (sessionData.sessionId) {
        await setSessionId(sessionData.sessionId)
      }
      else {
        await clearSession()
      }
    }

    user.value = client
    sessionStatus.value = 'authenticated'
    return sessionData
  }

  /**
   * URL of the API start endpoint, which 302s to the provider. `redirect` is the
   * path to return to (e.g. the checkout page) once authentication completes.
   * `display: 'popup'` is echoed back on the callback URL so the callback page
   * knows to hand its result to the opener instead of rendering in place.
   *
   * Kept synchronous: popup callers must open the window in the click handler
   * without awaiting anything, or the popup blocker fires.
   */
  function buildSocialLoginUrl(
    provider: 'google' | 'microsoft' | 'facebook',
    redirect?: string,
    display?: 'popup',
  ) {
    const config = useRuntimeConfig()
    const { deviceId } = useDeviceId()
    const apiUrl = (config.public.apiUrl as string).replace(/\/$/, '')

    const url = new URL(`${apiUrl}/whmcs/social/${provider}/start`)
    url.searchParams.set('deviceId', deviceId)
    if (redirect) {
      url.searchParams.set('redirect', redirect)
    }
    if (display) {
      url.searchParams.set('display', display)
    }
    return url.href
  }

  /** Begin a social login by full-page-redirecting the browser to the provider. */
  function startSocialLogin(provider: 'google' | 'microsoft' | 'facebook', redirect?: string) {
    window.location.href = buildSocialLoginUrl(provider, redirect)
  }

  /** Exchange the single-use handoff code from the social callback for a session. */
  async function exchangeSocialHandoff(code: string) {
    const { bffFetch } = useMwBff()
    const { deviceId } = useDeviceId()
    try {
      const data = await bffFetch<MwBffSessionResponse>('/whmcs/social/handoff/exchange', {
        method: 'POST',
        body: { code, deviceId },
      })
      return await applySessionData(data)
    } catch (error: any) {
      setSessionStatus('unauthenticated')
      throw error
    }
  }

  /** Complete a social signup with the collected WHMCS address fields. */
  async function completeSocialRegistration(payload: Record<string, unknown>) {
    const { bffFetch } = useMwBff()
    const { deviceId } = useDeviceId()
    try {
      const data = await bffFetch<MwBffSessionResponse>('/whmcs/social/complete-registration', {
        method: 'POST',
        // Browser locale so the WHMCS account is created with the visitor's
        // language (pt → portuguese-br, anything else → english).
        body: {
          language: import.meta.client ? navigator.language : undefined,
          ...payload,
          deviceId,
        },
      })
      return await applySessionData(data)
    } catch (error: any) {
      setSessionStatus('unauthenticated')
      throw error
    }
  }

  /** List manageable accounts for a pending multi-account social login. */
  async function getSocialSelectionAccounts(token: string) {
    const { bffFetch } = useMwBff()
    return bffFetch<MwBffAccountSelectionResponse['accounts']>(`/whmcs/social/selection/${token}`, {
      method: 'GET',
    })
  }

  /** Complete a multi-account social login once the user picked a client. */
  async function selectSocialAccount(token: string, clientId: number) {
    const { bffFetch } = useMwBff()
    const { deviceId } = useDeviceId()
    try {
      const data = await bffFetch<MwBffSessionResponse>('/whmcs/social/select-account', {
        method: 'POST',
        body: { token, clientId, deviceId },
      })
      return await applySessionData(data)
    } catch (error: any) {
      setSessionStatus('unauthenticated')
      throw error
    }
  }

  async function resendTwoFactor(challengeId: string) {
    const { bffFetch } = useMwBff()
    return bffFetch<{ result: string; resendCount: number; maxResends: number }>(
      '/whmcs/login/resend-two-factor',
      { method: 'POST', body: { challengeId } },
    )
  }

  async function verifyTwoFactor(challengeId: string, code: string | undefined, deviceId: string, rememberMe?: boolean, clientId?: number) {
    const { $mwApi } = useNuxtApp()
    const { isCapacitor, setSessionId, clearSession } = useBffSession()

    try {
      const response = await $mwApi.verifyTwoFactor({ challengeId, code: code as string, deviceId, rememberMe, clientId })
      const data = response.data as unknown as MwBffSessionResponse | MwBffAccountSelectionResponse

      if ('result' in data && data.result === 'account_selection_required') {
        return data as MwBffAccountSelectionResponse
      }

      const sessionData = data as MwBffSessionResponse
      const client = sessionData.client as ClientDetailsDto

      if (isCapacitor) {
        if (sessionData.sessionId) {
          await setSessionId(sessionData.sessionId)
        }
        else {
          await clearSession()
        }
      }

      user.value = client
      sessionStatus.value = 'authenticated'

      return sessionData
    } catch (error: any) {
      setSessionStatus('unauthenticated')
      throw error
    }
  }

  /**
   * The read in flight, shared by whoever asks while it is.
   *
   * The route guard, the layout and a page each resolve the signed-in customer,
   * and during one server render they did so one after the other: three
   * `/whmcs/me` calls per dashboard view, each a WHMCS round-trip of over a
   * second at the time. Created inside this closure on purpose — a setup
   * store's locals belong to the store INSTANCE, which on the server is one per
   * request, so this can never hand one customer's read to another's render.
   */
  const detailsFlight = createSingleFlight<ClientDetailsDto>()

  /**
   * Fetch the signed-in customer's details.
   *
   * @param options.fresh ask WHMCS now instead of taking the API's cached
   *   profile — for the one page that shows the profile back to its owner, who
   *   may have just edited it in WHMCS. Everything else wants the cached copy.
   */
  function clientDetails(options: { fresh?: boolean } = {}): Promise<ClientDetailsDto> {
    const read = () => {
      // Resolved synchronously, before anything is awaited: after an `await`
      // the Nuxt instance is no longer implicitly available on the server.
      const { $mwApi } = useNuxtApp()
      return (async () => {
        try {
          const response = await $mwApi.getUserDetails(options.fresh || undefined)
          user.value = response.data
          sessionStatus.value = 'authenticated'
          return response.data
        } catch (error: any) {
          if (error?.response?.status === 401) {
            setSessionStatus('unauthenticated')
          }
          throw error
        }
      })()
    }

    // A fresh read is its own request: it must not be answered by a cached one
    // that happens to be in flight.
    return options.fresh ? read() : detailsFlight.run(read)
  }

  /**
   * The signed-in customer, fetched only if this render does not hold them yet.
   *
   * What a layout or a page wants: by the time either runs, the route guard has
   * normally resolved the session, and asking again is a second request for an
   * answer already in the store. It still fetches when the guard failed open
   * (a transient error leaves `user` empty on purpose) — which is the one case
   * the layout's own read exists for.
   */
  async function ensureUser(): Promise<ClientDetailsDto> {
    if (user.value && sessionStatus.value === 'authenticated') return user.value
    return clientDetails()
  }

  async function ssoWhmcs(path?: string) {
    const { $mwApi } = useNuxtApp()
    try {
      const response = await $mwApi.createSsoToken(path)
      return response.data
    } catch (error: any) {
      if (error?.response?.status === 401) {
        setSessionStatus('unauthenticated')
      }
      throw error
    }
  }

  async function logout(deviceId?: string) {
    const { $mwApi } = useNuxtApp()
    const { isCapacitor, clearSession } = useBffSession()

    try {
      await $mwApi.logout({ deviceId })
    } finally {
      // Clear stored session information for Capacitor clients
      if (isCapacitor) {
        await clearSession()
      }
      setSessionStatus('unauthenticated')
    }
  }

  function setSessionStatus(status: 'unknown' | 'authenticated' | 'unauthenticated') {
    sessionStatus.value = status

    if (status === 'unauthenticated') {
      user.value = null
    }
  }

  function mapLanguageToLocale(language: string) {
    const normalized = language.trim().toLowerCase()

    // "default" means the client inherits the WHMCS system default language,
    // which only the API knows (WHMCS_DEFAULT_LANGUAGE) — it resolves it into
    // `platformLocale`. Returning null here lets the caller fall through
    // instead of wrongly treating "default" as English.
    if (!normalized || normalized === 'default') {
      return null
    }

    if (['pt', 'pt-pt', 'pt-br', 'portuguese', 'português', 'brazilian portuguese'].some(value => normalized.includes(value))) {
      return 'pt-PT'
    }

    return 'en-GB'
  }

  function mapCountryToLocale(countryCode: string) {
    if (!countryCode) {
      return null
    }

    switch (countryCode.trim().toUpperCase()) {
      case 'BR':
      case 'PT':
        return 'pt-PT'
      default:
        return 'en-GB'
    }
  }

  function getUserPreferredLocale() {
    const currentUser = user.value as any
    if (!currentUser) {
      return null
    }

    // Server-resolved locale (profile language when supported, the WHMCS
    // default for "DEFAULT" profiles, English otherwise). Present on both
    // /whmcs/me and the login response — always preferred when available.
    const platformLocale = currentUser?.platformLocale
    if (platformLocale === 'pt') {
      return 'pt-PT'
    }
    if (platformLocale === 'en') {
      return 'en-GB'
    }

    const language = currentUser?.client?.language ?? currentUser?.language
    if (typeof language === 'string') {
      const localeFromLanguage = mapLanguageToLocale(language)
      if (localeFromLanguage) {
        return localeFromLanguage
      }
    }

    const countrycode = currentUser?.client?.countrycode ?? currentUser?.countrycode
    if (typeof countrycode === 'string') {
      const localeFromCountry = mapCountryToLocale(countrycode)
      if (localeFromCountry) {
        return localeFromCountry
      }
    }

    return null
  }

  function setUserDefaultLanguage() {
    const locale = getUserPreferredLocale()
    if (!locale) {
      return
    }

    return $i18n.setLocale(locale)
  }

  async function setBrowserDefaultLanguage() {
    const setLocaleFromPrefix = (prefix: string | undefined | null) => {
      if (!prefix) {
        return $i18n.setLocale('en-GB')
      }

      switch (prefix.toLowerCase()) {
        case 'pt':
          return $i18n.setLocale('pt-PT')
        default:
          return $i18n.setLocale('en-GB')
      }
    }

    if (import.meta.client) {
      const language = navigator.language
      const prefix = language?.split('-')[0]
      return setLocaleFromPrefix(prefix)
    }

    const parseAcceptLanguage = function (acceptLanguage: string): string | null {
      if (!acceptLanguage) return null

      const languages = acceptLanguage.split(',').map(lang => {
        const parts = lang.split(';q=')
        return {
          lang: parts[0].trim(),
          q: parts[1] ? parseFloat(parts[1]) : 1.0
        }
      }).sort((a, b) => b.q - a.q)

      return languages[0]?.lang || null
    }

    const headers = useRequestHeaders(['accept-language'])
    const acceptLanguage = headers['accept-language'] || ''
    const preferredLanguage = parseAcceptLanguage(acceptLanguage)
    const prefix = preferredLanguage?.split('-')[0]
    return setLocaleFromPrefix(prefix)
  }

  return {
    user,
    sessionStatus,
    getUser,
    login,
    // A signup already carries its session (the API mints it from having just
    // created the account), so /register adopts it rather than logging in again.
    applySessionData,
    buildSocialLoginUrl,
    startSocialLogin,
    exchangeSocialHandoff,
    completeSocialRegistration,
    getSocialSelectionAccounts,
    selectSocialAccount,
    resendTwoFactor,
    verifyTwoFactor,
    clientDetails,
    ensureUser,
    ssoWhmcs,
    logout,
    getUserPreferredLocale,
    setUserDefaultLanguage,
    setBrowserDefaultLanguage,
    setSessionStatus,
  }
})
